import useCart from "grandus-lib/hooks/useCart";
import { Card, Table, Divider, Typography, Col } from "antd";
import map from "lodash/map";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import toNumber from "lodash/toNumber";
import upperFirst from "lodash/upperFirst";

import Link from "next/link";

import styles from "./CartSummary.module.scss";
import useWebInstance from "grandus-lib/hooks/useWebInstance";
import { GiftOutlined } from "@ant-design/icons";
import { useTranslation } from 'next-i18next';
import Image from "grandus-lib/components-atomic/image/Image";
import Price from "grandus-lib/components-atomic/price/Price";

const { Paragraph } = Typography;

const ItemCount = ({ item }) => {
  if (get(item, "count") === false) {
    return "";
  }

  return (
    <>
      {get(item, "measureUnit") ? `1 ${get(item, "measureUnit")} x ` : ""}
      <span>{get(item, "count", 1)}</span>&nbsp;{get(item, "store.name")}
    </>
  );
};

const Summary = ({
  actions = [],
  showProducts = true,
  title = null,
  simple = false,
  children,
}) => {
  const { t } = useTranslation();
  const { cart, isLoading } = useCart();
  const { settings } = useWebInstance();
  if (!isLoading && isEmpty(cart)) {
    return "Cart is empty";
  }

  const showWithoutVat = !isEmpty(get(cart, "vatNumber",null));
  
  const deliveryAndPayment = [];

  if (get(cart, "delivery")) {
    const deliveryGroupName = get(cart, "delivery.groupName");
    deliveryAndPayment.push({
      count: false,
      priceTotalData: get(cart, "delivery.priceData"),
      product: {
        photo: false,
        name: deliveryGroupName || get(cart, "delivery.name"),
      },
    });
  }

  if (get(cart, "payment")) {
    deliveryAndPayment.push({
      count: false,
      priceTotalData: get(cart, "payment.priceData"),
      product: {
        photo: false,
        name: `${get(cart, "payment.name")}`,
      },
    });
  }

  const tableColumns = [
    {
      title: "",
      responsive: ["sm"],
      render: (item) => {
        if (!get(item, "product.photo") || simple) {
          return "";
        }
        return (
          <Link
            href="/produkt/[id]"
            as={`/produkt/${get(item, "product.urlTitle")}`}
          >
            <a>
              {get(item, "product.photo") ? (
                <Image
                  photo={get(item, "product.photo")}
                  size={"50x50"}
                  type={"jpg"}
                />
              ) : (
                ""
              )}
            </a>
          </Link>
        );
      },
    },
    {
      title: upperFirst(t('cart:item')),
      render: (item) => {
        return (
          <>
            <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
              {get(item, "product.urlTitle") ? (
                <Link
                  href="/produkt/[id]"
                  as={`/produkt/${get(item, "product.urlTitle")}`}
                >
                  <a>{get(item, "product.name")}</a>
                </Link>
              ) : (
                get(item, "product.name")
              )}
            </Paragraph>
            {!isEmpty(get(item, "hookedItems")) ? (
              <div className={styles.hook}>
                <GiftOutlined />
                {map(get(item, "hookedItems"), (hook, index) => (
                  <span
                    key={`product-hook-${get(
                      item,
                      "product.urlTitle"
                    )}-${index}`}
                    className={styles.item}
                  >
                    {get(hook, "product.name")}
                  </span>
                ))}
              </div>
            ) : (
              ""
            )}
            {!isEmpty(get(item, "cartHook")) ? (
              <div className={styles.hook}>
                <GiftOutlined />
                <span className={styles.item}>
                  {get(item, "cartHook.name")}
                </span>
              </div>
            ) : (
              ""
            )}            
            <Col xs={24} sm={0}>
              <ItemCount item={item} />
            </Col>
          </>
        );
      },
    },
    {
      title: upperFirst(t('cart:amount')),
      responsive: ["md"],
      render: (item) => {
        if (simple) {
          return "";
        }
        return <ItemCount item={item} />;
      },
    },
    {
      title: upperFirst(t('cart:price')),
      align: "right",
      render: (item) => (
        <>
          <b style={{ whiteSpace: "nowrap" }}>
            <Price
              priceData={get(item, "priceTotalData")}
              options={{ hideVatPrice: !showWithoutVat }}
            />
          </b>
        </>
      ),
    },
  ];

  return (
    <Card title={title || upperFirst(t('cart:order summary'))} className={styles?.summaryCard}>
      {showProducts || !isEmpty(deliveryAndPayment) ? (
        <Table
          className={styles.table}
          showHeader={false}
          pagination={false}
          columns={tableColumns}
          rowKey={(record) => (record?.id ? record?.id : record?.product?.id)}
          dataSource={
            showProducts
              ? get(cart, "items", [])
                  .concat(get(cart, "extraItems", []))
                  .concat(deliveryAndPayment)
              : deliveryAndPayment
          }
        />
      ) : (
        ""
      )}
      <ul className={styles.itemsList}>
        {cart?.coupon ? (
          <>
            <li>
              {upperFirst(t('common:discount'))} {get(cart, "coupon.hash")}
              <Price
                priceData={get(cart, "coupon.discountData")}
                options={{ hideVatPrice: !showWithoutVat }}
              />
            </li>
            <li>
              <Divider style={{ margin: "15px 0" }} />
            </li>
          </>
        ) : (
          ""
        )}
        {!isEmpty(cart?.duties) ? (
          <>
            {map(cart?.duties, (duty) => (
              <li key={`duty-${duty?.id}`}>
                {get(duty, "name")}
                <Price
                  priceData={get(duty, "priceData")}
                  options={{ hideVatPrice: !showWithoutVat }}
                />
              </li>
            ))}

            <li>
              <Divider style={{ margin: "15px 0" }} />
            </li>
          </>
        ) : (
          ""
        )}
        {!isEmpty(cart?.discounts) ? (
          <>
            {map(cart?.discounts, (discount) => (
              <li key={`discount-${discount?.id}`}>
                {get(discount, "name")}
                <Price
                  priceData={get(discount, "discountData")}
                  options={{ hideVatPrice: !showWithoutVat }}
                />
              </li>
            ))}

            <li>
              <Divider style={{ margin: "15px 0" }} />
            </li>
          </>
        ) : (
          ""
        )}
        {toNumber(settings?.credits_allow_user) && cart?.credit ? (
          <>
            <li>
              {get(settings, "credits_name") || upperFirst(t('cart:credits'))}
              <Price
                priceData={{
                  price: get(cart, "creditValue"),
                  currencySymbol: get(cart, "sumData.currencySymbol"),
                }}
                options={{ hideVatPrice: !showWithoutVat }}
              />
            </li>
            <li>
              <Divider style={{ margin: "15px 0" }} />
            </li>
          </>
        ) : (
          ""
        )}
        <li className={styles.totalWithoutVAT}>
          {upperFirst(t('cart:total price without VAT'))}
          <Price
            priceData={{
              ...get(cart, "sumData"),
              price: get(cart, "sumData.priceWithoutVat"),
              priceFormatted: false,
            }}
            options={{ hideVatPrice: !showWithoutVat }}
            className={styles?.price}
          />
        </li>
        <li className={styles.totalWithVAT}>
          {upperFirst(t('cart:total price'))}
          <Price
            priceData={get(cart, "sumData")}
            options={{ hideVatPrice: !showWithoutVat }}
            className={styles?.price}
          />
        </li>
      </ul>
      {children}
      {!isEmpty(actions) ? (
        <>
          <Divider />
          <div className={styles.actions}>
            {map(actions, (action, index) => (
              <span
                key={`cart-summary-actions-${index}`}
                style={{ width: actions.length === 1 ? "100%" : "auto" }}
              >
                {action}
              </span>
            ))}
          </div>
        </>
      ) : (
        ""
      )}
    </Card>
  );
};

export default Summary;
